import type {
  Research_Studies_Bool_Exp,
  StudiesFilterQuery,
  StudiesFilterQueryVariables,
  StudyResultsQuery,
  StudyResultsQueryVariables,
} from 'graphql/research/types-and-hooks';
import type { QueryError, QueryProps } from 'lib/api';

import {
  refetchStudyResultsQuery as fetchStudyResultsQuery,
  refetchCountStudiesByFilterQuery as fetchCountStudiesByFilterQuery,
  refetchStudiesFilterQuery as fetchStudiesFilterQuery,
} from 'graphql/research/types-and-hooks';
import { queryResearch } from 'lib/apollo';

export type GetResultsAggregate = {
  count: number;
  nodes: StudyResultsQuery['nodes'];
};

export async function getResultsAggregate({
  client,
  variables = {},
}: {
  client: QueryProps['client'];
  variables?: StudyResultsQueryVariables;
}): Promise<GetResultsAggregate | QueryError> {
  const { data, error } = await queryResearch<
    StudyResultsQuery,
    StudyResultsQueryVariables
  >(client, fetchStudyResultsQuery(variables));

  if (error) {
    return { error };
  }

  return {
    count: data?.pagination.aggregate.count,
    nodes: data?.nodes || [],
  };
}

export type GetResultCount = {
  count: number;
};

export async function getResultCount({
  client,
  result,
  filters = {},
}: {
  client: QueryProps['client'];
  result: string;
  filters?: Partial<Research_Studies_Bool_Exp>;
}): Promise<GetResultCount> {
  const { data, error } = await queryResearch<
    StudiesFilterQuery,
    StudiesFilterQueryVariables
  >(
    client,
    fetchCountStudiesByFilterQuery({
      filters: {
        ...filters,
        result: { _eq: result },
      } as Research_Studies_Bool_Exp,
    }),
  );

  if (error) {
    console.log(error);
    return { count: 0 };
  }

  return {
    count: data?.pagination.aggregate.count || 0,
  };
}

export type StudyResult = StudyResultsQuery['nodes'][0] & {
  count: number;
};

export type GetResultsAggregateCount = StudyResult[];

export async function getResultsAggregateCount({
  client,
  filters,
}: {
  client: QueryProps['client'];
  filters?: Research_Studies_Bool_Exp;
}): Promise<GetResultsAggregateCount | QueryError> {
  const resultsData = await getResultsAggregate({
    client,
    variables: { filters },
  });

  if ((resultsData as QueryError)?.error) {
    return { error: (resultsData as QueryError)?.error };
  }

  const data = resultsData as GetResultsAggregate;

  if (data?.count <= 0) {
    return [];
  }

  const resultsPromise = await Promise.allSettled<StudyResult>(
    (data?.nodes || []).map(async item => {
      try {
        const { count } = await getResultCount({
          client,
          result: item.result,
          filters,
        });

        return { ...item, count };
      } catch (error) {
        throw item;
      }
    }),
  );

  const results: GetResultsAggregateCount = resultsPromise
    .map(({ status, ...result }) => {
      if (status === 'rejected') {
        return {
          ...(result as PromiseRejectedResult).reason,
          count: 0,
        };
      }

      return (result as PromiseFulfilledResult<StudyResult>).value;
    })
    .sort((a, b) => b.count - a.count);

  return results;
}

export type GetStudiesAggregate = {
  count: number;
  nodes?: StudiesFilterQuery['nodes'];
};

export async function getStudiesAggregate({
  client,
  variables = {},
}: {
  client: QueryProps['client'];
  variables?: Omit<StudiesFilterQueryVariables, 'filters'> & {
    filters?: Partial<Research_Studies_Bool_Exp>;
  };
}): Promise<GetStudiesAggregate | QueryError> {
  const { data, error } = await queryResearch<
    StudiesFilterQuery,
    StudiesFilterQueryVariables
  >(client, fetchStudiesFilterQuery(variables as StudiesFilterQueryVariables));

  if (error) {
    return { error };
  }

  return {
    count: data?.pagination.aggregate.count,
    nodes: data?.nodes || [],
  };
}
